import { z } from "zod";
import { MEMBER_AUDIENCE_REPORT_KIND, REPORT_PDF_KINDS, REPORT_PDF_LOCALES, isVnCalendarYmd } from "@goppy/domain";

/* ===========================================================================
 * US-E5.F1-0077 Export Report (CSV streaming R2) — @UC0048, Phase 9 Reports (read-only).
 *
 * POST /v1/funds/:id/reports/export — admin/treasurer/owner yêu cầu xuất báo cáo giao dịch quỹ
 *   theo khoảng thời gian → tạo report job status=pending, enqueue worker → 202 { reportId, status }.
 * GET /v1/funds/:id/reports/:reportId — poll trạng thái job + signedUrl khi done.
 *
 * READ-ONLY: chỉ đọc ledger (KHÔNG post). Worker stream batched cursor → CSV → R2 (không OOM).
 *
 * 🔴 TỆP CSV MỞ ĐẦU BẰNG BOM UTF-8 (US-E23.F1-0759) — phần của hợp đồng, không phải chi tiết cài
 * đặt. Excel trên Windows bấm-đúp KHÔNG đoán UTF-8; thiếu ba byte đó thì mọi tên tiếng Việt trong
 * tệp hiện thành ký tự rác, và tệp vẫn "hợp lệ" nên không lỗi nào bắn ra. Bộ đọc CSV của chính repo
 * (`admin-member-import.ts`) đã gỡ BOM khi NHẬP, nên hai đầu khớp nhau.
 * Redaction: story chốt admin/treasurer/owner only; CSV chỉ chứa field giao dịch (KHÔNG PII member,
 * KHÔNG số dư private của người khác). Tiền STRING minor unit (B1, no float).
 * ========================================================================= */

/** Định dạng YYYY-MM-DD (khớp filter transactions 0054). */
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * 🔴 REPORT-EXPORT-VN-DAY-BOUNDARY B1 (QĐ PO 01/10/2026) — tham số NGÀY của báo cáo/xuất sổ/lọc giao dịch: `YYYY-MM-DD`, **ngày lịch
 * Asia/Ho_Chi_Minh**, và phải là ngày CÓ THẬT (`isVnCalendarYmd`, domain — cùng luật bộ đổi ngày⇒mốc `vnDayStartUtc`). Hình dây không đổi
 * (chuỗi YYYY-MM-DD); "2026-02-30" trước đây qua regex rồi trôi im lặng sang 02/03 — nay 400 ở biên. Review #1120 MED-1: vị-từ nay đòi dựng
 * được CẢ HAI biên ⇒ `9999-12-31` (hết ngày = năm 10000) cũng 400 `VALIDATION_FAILED` ở biên thay vì câu trần của service.
 */
export const vnCalendarDaySchema = z.string().regex(DATE_PATTERN).refine(isVnCalendarYmd, { message: "Ngày không có thật." });

/** Trạng thái report job. pending→ (worker) →done | failed. */
export const REPORT_STATUS = {
  PENDING: "pending",
  DONE: "done",
  FAILED: "failed",
} as const;
export type ReportStatus = (typeof REPORT_STATUS)[keyof typeof REPORT_STATUS];
export const reportStatusSchema = z.enum([
  REPORT_STATUS.PENDING,
  REPORT_STATUS.DONE,
  REPORT_STATUS.FAILED,
]);

/**
 * Định dạng xuất. `csv` cho ba báo cáo dữ liệu (giao dịch/thành viên/hoạt động — GIỮ NGUYÊN, không
 * còn nút trên mobile nhưng engine + màn "Bản xuất" vẫn đọc được bản cũ); `pdf` cho hai báo cáo
 * thu/chi (US-E23.F1-0789). Client KHÔNG chọn format — nó là thuộc tính của `kind`
 * (`reportFormatOfKind`), nên không có ca "xin PDF của danh sách thành viên" để phải từ chối.
 */
export const REPORT_FORMAT = { CSV: "csv", PDF: "pdf" } as const;
export type ReportFormat = (typeof REPORT_FORMAT)[keyof typeof REPORT_FORMAT];
export const reportFormatSchema = z.enum([REPORT_FORMAT.CSV, REPORT_FORMAT.PDF]);

/**
 * Ngôn ngữ của bản PDF (US-E23.F1-0789 · QĐ người dùng 15/09/2026: vi · en · hoặc CẢ HAI = hai tệp
 * riêng ⇒ client tạo HAI job). Tập đóng mirror `REPORT_PDF_LOCALES` (@goppy/domain — worker render
 * theo cùng hằng) + CHECK `report_jobs_locale_check`.
 */
export const REPORT_LOCALE = { VI: "vi", EN: "en" } as const satisfies Record<string, (typeof REPORT_PDF_LOCALES)[number]>;
export type ReportLocale = (typeof REPORT_LOCALE)[keyof typeof REPORT_LOCALE];
export const reportLocaleSchema = z.enum([REPORT_LOCALE.VI, REPORT_LOCALE.EN]);

/**
 * Loại nội dung xuất ở cấp NHÓM. Whitelist đóng — mirror CHECK `report_jobs_kind_check`.
 *
 * 🔴 US-E23.F1-0789 (QĐ người dùng 15/09/2026): mobile CHỈ còn hai nút "Tạo báo cáo thu" / "Tạo báo
 * cáo chi" (PDF). Ba kind CSV GIỮ NGUYÊN trong hợp đồng: job cũ trong `report_jobs` vẫn phải đọc
 * được ở màn "Bản xuất", trợ lý AI vẫn xuất `transactions`, và engine CSV không bị xoá — chỉ không
 * còn nút bấm.
 */
export const GROUP_REPORT_KIND = {
  /** Lịch sử giao dịch gộp MỌI quỹ chưa lưu trữ của nhóm (CSV). */
  TRANSACTIONS: "transactions",
  /** Danh sách thành viên (CSV, không PII nhạy cảm — xem GROUP_MEMBERS_CSV_COLUMNS). */
  MEMBERS: "members",
  /** Báo cáo hoạt động: mỗi quỹ một dòng tổng hợp (CSV). */
  ACTIVITY: "activity",
  /** Báo cáo THU (PDF, bản tách 0789 — GIỮ cho job cũ, không còn nút tạo mới trên mobile). */
  INCOME: "income",
  /** Báo cáo CHI (PDF, bản tách 0789 — GIỮ cho job cũ, không còn nút tạo mới trên mobile). */
  EXPENSE: "expense",
  /**
   * 🔴 US-E23.F1-0797 (QĐ người dùng 16/09/2026) — Báo cáo THU CHI (PDF): MỘT tệp, hai phần THU rồi
   * CHI, mỗi phần có tiêu đề riêng + vạch ngăn. Nút duy nhất ở mobile ("Tạo báo cáo thu chi").
   */
  FINANCE: "finance",
} as const;
export type GroupReportKind = (typeof GROUP_REPORT_KIND)[keyof typeof GROUP_REPORT_KIND];
export const groupReportKindSchema = z.enum([
  GROUP_REPORT_KIND.TRANSACTIONS,
  GROUP_REPORT_KIND.MEMBERS,
  GROUP_REPORT_KIND.ACTIVITY,
  GROUP_REPORT_KIND.INCOME,
  GROUP_REPORT_KIND.EXPENSE,
  GROUP_REPORT_KIND.FINANCE,
]);

/** Các kind PDF — CÙNG hằng với worker (`REPORT_PDF_KINDS` @goppy/domain), không chép chuỗi. */
export const PDF_REPORT_KINDS: ReadonlySet<GroupReportKind> = new Set<GroupReportKind>(REPORT_PDF_KINDS);

/**
 * 🔴 US-E23.F1-0867 (@UC0048 · QĐ PO 30/09/2026 — *"ai cũng xuất được sổ thu chi"*) — kind mà MEMBER THƯỜNG được xuất:
 * đúng `finance` (PDF thu chi), nội dung lọc theo tầm nhìn của người xuất ở worker (`report_jobs.viewerScope`).
 * Mọi kind khác (CSV · hai PDF tách cũ) GIỮ O/A/T — QĐ "xuất CSV / dữ liệu khác giữ nguyên". CÙNG hằng với CHECK
 * `report_jobs_member_audience_check` (qua `MEMBER_AUDIENCE_REPORT_KIND` của domain) và cổng ở `ReportsService.createGroupExport`.
 */
export const MEMBER_EXPORTABLE_GROUP_REPORT_KINDS: ReadonlySet<GroupReportKind> = new Set<GroupReportKind>([
  MEMBER_AUDIENCE_REPORT_KIND,
]);

/**
 * MỘT chỗ trả lời "kind này ra tệp gì". Service ghi `format` từ đây, worker chọn renderer theo `kind`,
 * CHECK `report_jobs_kind_format_check` khoá cùng ánh xạ ở DB — ba tầng, một luật.
 */
export function reportFormatOfKind(kind: GroupReportKind): ReportFormat {
  return PDF_REPORT_KINDS.has(kind) ? REPORT_FORMAT.PDF : REPORT_FORMAT.CSV;
}

/**
 * Body POST export — khoảng thời gian [from, to] (YYYY-MM-DD, ngày lịch Asia/Ho_Chi_Minh, inclusive cuối ngày — `to` tính trọn
 * ngày đó tới 24:00 giờ VN; QĐ PO 01/10/2026, trước đó là ngày UTC). Cả hai optional (vắng = toàn bộ). `.strict()` từ chối field lạ
 * (C5). superRefine: from ≤ to nếu có cả hai.
 */
export const exportReportRequestSchema = z
  .object({
    from: vnCalendarDaySchema.optional(),
    to: vnCalendarDaySchema.optional(),
  })
  .strict()
  .superRefine((v, ctx) => {
    if (v.from && v.to && v.from > v.to) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "from phải ≤ to.",
        path: ["from"],
      });
    }
  });
export type ExportReportRequest = z.infer<typeof exportReportRequestSchema>;

/** Response 202 khi tạo job — reportId để client poll + trạng thái ban đầu (pending). */
export const exportReportResponseSchema = z
  .object({
    reportId: z.string().uuid(),
    status: reportStatusSchema,
  })
  .strict();
export type ExportReportResponse = z.infer<typeof exportReportResponseSchema>;

/**
 * Response poll GET report. `signedUrl` chỉ có khi done (TTL — presigned R2). failed → error message
 * (KHÔNG lộ chi tiết PII/hạ tầng). rowCount báo số dòng dữ liệu đã xuất khi done.
 */
export const reportStatusResponseSchema = z
  .object({
    reportId: z.string().uuid(),
    status: reportStatusSchema,
    /**
     * US-E23.F1-0789 — `kind` + `locale` đi kèm khi poll: mobile mở từ cú chạm push chỉ có
     * `reportId`, và tên tệp (`goppy-bao-cao-thu-<ngày>.pdf`) cần cả hai. Job cấp quỹ luôn
     * `transactions` / `locale: null`.
     */
    kind: groupReportKindSchema,
    locale: reportLocaleSchema.nullable(),
    format: reportFormatSchema,
    from: z.string().regex(DATE_PATTERN).nullable(),
    to: z.string().regex(DATE_PATTERN).nullable(),
    signedUrl: z.string().nullable(),
    rowCount: z.number().int().nonnegative().nullable(),
    createdAt: z.string().datetime(),
  })
  .strict();
export type ReportStatusResponse = z.infer<typeof reportStatusResponseSchema>;

/**
 * Cột CSV báo cáo giao dịch (thứ tự cố định — header khớp). Redaction: KHÔNG có cột PII member/số dư
 * người khác — chỉ field giao dịch ledger (id/loại/hướng/số tiền/tiền tệ/memo/thời gian).
 *
 * 🔴 US-E23.F1-0759 — `direction` có **BA** giá trị: `income` · `expense` · `neutral`
 * (`REPORT_DIRECTIONS` ở `@goppy/domain`). Trước bản này nó nhị phân, nên 21/28 txType — gồm
 * `FUND_TRANSFER_IN` (tiền VÀO) và `VALUE_FORM_SWITCH` (Σ CASH bất biến) — đều bị dán nhãn
 * `expense`, và ai cộng cột đó trong Excel ra số chi phồng lên.
 *
 * 🔴 `created_at` (REPORT-EXPORT-VN-DAY-BOUNDARY B2, 01/10/2026 — đổi hợp đồng TỆP): ISO 8601 theo giờ **Asia/Ho_Chi_Minh kèm offset**
 * (`2026-10-01T00:30:00.000+07:00`). Trước: UTC `…Z` (`2026-09-30T17:30:00.000Z`) — cùng instant, nhưng trong tệp kỳ "tháng 10" (ngày lịch VN
 * từ B1) người đọc thấy ngày 30/09. Máy đọc lại (`Date.parse`, Excel/Sheets nhận ISO có offset) ra ĐÚNG mili-giây cũ.
 */
export const REPORT_CSV_COLUMNS = [
  "transaction_id",
  "type",
  "direction",
  "amount_minor",
  "currency",
  "memo",
  "created_at",
] as const;
export type ReportCsvColumn = (typeof REPORT_CSV_COLUMNS)[number];

/* ===========================================================================
 * US-E23.F1-0331-S09 (@UC9514) — EXPORT CẤP NHÓM.
 *
 * POST /v1/groups/:id/reports/export  ·  GET /v1/groups/:id/reports/:reportId
 *
 * 🔴 TÁI DÙNG ENGINE CẤP QUỸ: cùng bảng `report_jobs`, cùng worker `report-export`, cùng đường R2 +
 * presigned URL. Chỉ MỞ RỘNG scope + thêm `kind`. Engine export thứ hai sẽ nghĩa là hai chỗ áp
 * redaction — và chỗ nào quên áp thì rò số tiền của nhóm ra file tải về được.
 *
 * Figma đặt BA mục xuất ở cấp nhóm; cả ba trả về CÙNG shape response với export cấp quỹ (client
 * poll một kiểu duy nhất).
 * ========================================================================= */

/**
 * Body POST export cấp nhóm. `kind` BẮT BUỘC — không default: xuất nhầm loại báo cáo cho cả nhóm là
 * lỗi im lặng (file tải về trông vẫn "hợp lệ"), nên bắt khai tường minh.
 * `from`/`to` chỉ có nghĩa với `kind='transactions'`; hai kind còn lại là ảnh chụp hiện trạng. Ngày = ngày lịch Asia/Ho_Chi_Minh,
 * `to` inclusive cuối ngày (cùng luật `exportReportRequestSchema`).
 */
export const exportGroupReportRequestSchema = z
  .object({
    kind: groupReportKindSchema,
    from: vnCalendarDaySchema.optional(),
    to: vnCalendarDaySchema.optional(),
    /**
     * US-E23.F1-0789 — ngôn ngữ bản PDF. BẮT BUỘC với kind PDF (không default: xuất nhầm ngôn ngữ
     * là lỗi im lặng y như xuất nhầm loại) và BỊ TỪ CHỐI với kind CSV (cột CSV là mã máy, không có
     * bản dịch — nhận rồi lờ đi là hứa một điều không làm).
     */
    locale: reportLocaleSchema.optional(),
  })
  .strict()
  .superRefine((v, ctx) => {
    if (v.from && v.to && v.from > v.to) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "from phải ≤ to.", path: ["from"] });
    }
    if (v.kind !== GROUP_REPORT_KIND.TRANSACTIONS && (v.from || v.to)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        // Nhận rồi lờ đi là cách chắc chắn nhất để người dùng tin file đã lọc theo ngày trong khi không.
        message: "Chỉ báo cáo giao dịch mới lọc theo khoảng thời gian.",
        path: ["from"],
      });
    }
    const isPdf = PDF_REPORT_KINDS.has(v.kind);
    if (isPdf && !v.locale) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Báo cáo PDF phải chọn ngôn ngữ.", path: ["locale"] });
    }
    if (!isPdf && v.locale) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Chỉ báo cáo PDF mới có ngôn ngữ.", path: ["locale"] });
    }
  });
export type ExportGroupReportRequest = z.infer<typeof exportGroupReportRequestSchema>;

/**
 * Cột CSV danh sách thành viên. Redaction: KHÔNG email/số điện thoại/địa chỉ — file này chia sẻ được
 * ra ngoài nhóm bằng một cú kéo thả, và PII công dân VN không được rời hệ thống theo đường đó.
 *
 * 🔴 US-E23.F1-0759 — TẬP DÒNG: chỉ **suất đang sống** (`active` + `unclaimed`, xem
 * `ROSTER_MEMBER_STATUSES` ở `@goppy/domain`). Bản trước không lọc gì, nên file mang theo cả người
 * đã rời và bị gỡ khỏi nhóm — trái đúng câu đã ghi ở `MEMBER_LIST_STATUS_SEATS`: *"Danh sách thành
 * viên là danh sách người đang gánh nghĩa vụ, không phải lịch sử ai từng ở đây."*
 */
export const GROUP_MEMBERS_CSV_COLUMNS = [
  "member_id",
  "display_name",
  "role",
  "status",
  "joined_at",
] as const;
export type GroupMembersCsvColumn = (typeof GROUP_MEMBERS_CSV_COLUMNS)[number];

/**
 * Cột CSV báo cáo hoạt động: mỗi quỹ một dòng. Tiền là STRING minor (B1 no-float).
 *
 * 🔴 US-E23.F1-0759 (QĐ người dùng 10/09/2026) — cột cuối ĐỔI TÊN `member_count` →
 * `due_member_count`, và ĐỔI CẢ NGHĨA. Nó từng nhận **sĩ số `active` của CẢ NHÓM**, đếm một lần rồi
 * in y hệt ở mọi dòng; đặt cạnh `fund_id` thì đọc thành *"quỹ này có N người"*. Nay là số suất
 * phân biệt có `member_dues` thuộc CHÍNH quỹ đó. `0` = quỹ chưa có đợt thu nào.
 */
export const GROUP_ACTIVITY_CSV_COLUMNS = [
  "fund_id",
  "fund_name",
  "currency",
  "transaction_count",
  "due_member_count",
] as const;
export type GroupActivityCsvColumn = (typeof GROUP_ACTIVITY_CSV_COLUMNS)[number];

/* ===========================================================================
 * US-E24.F10-0359-S01 (@UC9598) — GET /v1/groups/:id/reports · GET /v1/funds/:id/reports.
 *
 * VÌ SAO CÓ: export là ASYNC 202 — người dùng bấm, đóng app, mở lại. `reportId` chỉ nằm trong bộ
 * nhớ client, và đường đọc DUY NHẤT là `GET .../reports/:reportId` (poll theo id ĐÃ BIẾT). Kill app
 * trước khi client kịp persist ⇒ job vẫn chạy, file vẫn nằm trên R2, KHÔNG AI LẤY ĐƯỢC. Bấm export
 * lại ⇒ mỗi lần một job mới ⇒ R2 tích rác mà không ai biết có bao nhiêu bản đang treo.
 * Hai `@@index([fundId, createdAt])` / `([groupId, createdAt])` đã có sẵn — index chỉ có nghĩa cho
 * truy vấn LIST theo thời gian. Hạ tầng cho list đã chuẩn bị, chỉ thiếu endpoint.
 *
 * 🔴 KHÔNG `objectKey`, KHÔNG presigned URL trong list. `objectKey` là con trỏ R2 bền
 * ("lưu KEY bền, KHÔNG URL"). Link tải trong một response list = N link sống cùng lúc trong
 * log/cache client, TTL không kiểm soát được. Muốn tải ⇒ vẫn qua `GET .../reports/:reportId`.
 * List chỉ trả cờ `hasFile` — đủ để UI biết nút "Tải" có bật được không, và không hơn.
 * ========================================================================= */

/** Trần limit + độ dài cursor (mirror các list khác của repo). */
export const REPORT_JOBS_MAX_LIMIT = 50;
export const REPORT_JOBS_MAX_CURSOR_LENGTH = 512;

/**
 * Lưới quét link tải rò vào list (dùng trong test). Cố ý CHỈ bắt scheme http/https — hẹp và ít
 * dương-tính-giả, vì một guard hay báo động giả là guard sẽ bị tắt.
 */
export const REPORT_JOB_URL_SHAPE = /https?:\/\//;

/**
 * 1 dòng job trong danh sách — METADATA JOB, KHÔNG phải nội dung báo cáo ⇒ **0 trường tiền**.
 *
 * `error`: cột DB có comment "KHÔNG PII/hạ tầng"; worker ghi đúng một chuỗi hằng khi hỏng. Trả ra
 * là để người dùng biết bản xuất đó chết chứ không phải đang treo — nhưng test khoá lại: nội dung
 * không được mang đường dẫn file/hostname/stack.
 * `hasFile`: thay cho `objectKey`. Trạng thái `done` mà file rỗng là ca có thật (job hỏng nửa
 * chừng), nên cờ này KHÔNG suy từ `status` — nó là sự thật riêng.
 */
export const reportJobListItemSchema = z
  .object({
    reportId: z.string().uuid(),
    status: reportStatusSchema,
    /** Whitelist ĐÓNG, mirror CHECK `report_jobs_kind_check`. Job cấp quỹ luôn `transactions`. */
    kind: groupReportKindSchema,
    /** US-E23.F1-0789 — ngôn ngữ bản PDF (nhãn "Báo cáo thu · Tiếng Việt"); job CSV ⇒ null. */
    locale: reportLocaleSchema.nullable(),
    format: reportFormatSchema,
    from: z.string().regex(DATE_PATTERN).nullable(),
    to: z.string().regex(DATE_PATTERN).nullable(),
    rowCount: z.number().int().nonnegative().nullable(),
    error: z.string().nullable(),
    hasFile: z.boolean(),
    createdAt: z.string().datetime(),
  })
  .strict();

export type ReportJobListItem = z.infer<typeof reportJobListItemSchema>;

/** Envelope cursor chuẩn repo. 0 job ⇒ `data: []` (KHÔNG 404 — "chưa xuất lần nào" là sự thật hợp lệ). */
export const reportJobsListResponseSchema = z
  .object({
    data: z.array(reportJobListItemSchema),
    pageInfo: z
      .object({ hasNext: z.boolean(), nextCursor: z.string().nullable() })
      .strict(),
  })
  .strict();

export type ReportJobsListResponse = z.infer<typeof reportJobsListResponseSchema>;

/**
 * Query: cursor + limit + filter `status`.
 * 🔴 `status` rác ⇒ 400 tại biên, KHÔNG rơi vào nhánh "không lọc". Nhận rồi lờ đi là cách chắc chắn
 * nhất để người dùng tin danh sách đã lọc trong khi không.
 */
export const reportJobsListQuerySchema = z.object({
  cursor: z.string().max(REPORT_JOBS_MAX_CURSOR_LENGTH).optional(),
  limit: z.coerce.number().int().min(1).max(REPORT_JOBS_MAX_LIMIT).optional(),
  status: reportStatusSchema.optional(),
});

export type ReportJobsListQuery = z.infer<typeof reportJobsListQuerySchema>;
